import { Pagination } from '@heroui/react';
import { cn } from '../utils';

export interface PagerProps {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  itemLabel?: string;
  className?: string;
}

type PageEntry = number | 'ellipsis-left' | 'ellipsis-right';

const buildPageList = (page: number, totalPages: number): PageEntry[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: PageEntry[] = [1];
  if (page > 3) pages.push('ellipsis-left');

  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  for (let i = start; i <= end; i += 1) pages.push(i);

  if (page < totalPages - 2) pages.push('ellipsis-right');
  pages.push(totalPages);
  return pages;
};

export const Pager = ({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  itemLabel = 'results',
  className
}: PagerProps) => {
  if (totalPages <= 1) return null;

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  return (
    <Pagination className={cn('mt-6 w-full', className)}>
      <Pagination.Summary>
        Showing {startItem}–{endItem} of {totalItems} {itemLabel}
      </Pagination.Summary>
      <Pagination.Content>
        <Pagination.Item>
          <Pagination.Previous isDisabled={page <= 1} onPress={() => onPageChange(page - 1)}>
            <Pagination.PreviousIcon />
            <span>Prev</span>
          </Pagination.Previous>
        </Pagination.Item>
        {buildPageList(page, totalPages).map((entry) =>
          entry === 'ellipsis-left' || entry === 'ellipsis-right' ? (
            <Pagination.Item key={entry}>
              <Pagination.Ellipsis />
            </Pagination.Item>
          ) : (
            <Pagination.Item key={entry}>
              <Pagination.Link isActive={entry === page} onPress={() => onPageChange(entry)}>
                {entry}
              </Pagination.Link>
            </Pagination.Item>
          )
        )}
        <Pagination.Item>
          <Pagination.Next isDisabled={page >= totalPages} onPress={() => onPageChange(page + 1)}>
            <span>Next</span>
            <Pagination.NextIcon />
          </Pagination.Next>
        </Pagination.Item>
      </Pagination.Content>
    </Pagination>
  );
};
